import React, { useState, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../utils/useLanguage";
import arrowIcon from "../assets/previous_icon.png";

const CollapsibleGroupContext = createContext(null);

const useCollapsibleGroup = () => {
  const context = useContext(CollapsibleGroupContext);
  if (!context) {
    throw new Error("Component must be used within CollapsibleGroup");
  }
  return context;
};

export const CollapsibleList = ({ children, className = "" }) => {
  const { currentDirection } = useLanguage();

  return (
    <div
      className={`c-fabric-table o-column o-column--gap-size_400 u-margin u-margin--bottom_700 ${className}`}
      dir={currentDirection}
    >
      {children}
    </div>
  );
};

export const CollapsibleGroup = ({
  children,
  groupKey,
  defaultCollapsed = true,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const toggleCollapse = () => setIsCollapsed((prev) => !prev);

  return (
    <CollapsibleGroupContext.Provider
      value={{ isCollapsed, toggleCollapse, groupKey }}
    >
      {children}
    </CollapsibleGroupContext.Provider>
  );
};

export const CollapsibleHeader = ({ children, icon = arrowIcon }) => {
  const { isCollapsed, toggleCollapse } = useCollapsibleGroup();

  return (
    <div className="c-fabric-header" onClick={toggleCollapse}>
      {icon && (
        <span className="c-collapse-icon" style={{ cursor: "pointer" }}>
          <img
            src={icon}
            alt="Toggle"
            style={{
              width: 16,
              transform: isCollapsed ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.3s ease",
            }}
          />
        </span>
      )}
      <div className="o-column o-column--gap-size_200">{children}</div>
    </div>
  );
};

export const CollapsibleContent = ({ children }) => {
  const { isCollapsed } = useCollapsibleGroup();

  if (isCollapsed) return null;

  return <>{children}</>;
};

export const CollapsibleLabel = ({ label, value, translate = true }) => {
  const { t } = useTranslation("general");

  return (
    <span className="c-fabric-code">
      {translate ? t(label) : label}: {value}
    </span>
  );
};

export const Table = ({ children, className = "" }) => {
  return (
    <table className={`c-fabric-inner-table ${className}`}>{children}</table>
  );
};

export const TableHeader = ({ children }) => {
  return (
    <thead>
      <tr>{children}</tr>
    </thead>
  );
};

export const TableHeaderCell = ({ label, translate = true }) => {
  const { t } = useTranslation("general");

  return <th>{translate ? t(label) : label}</th>;
};

export const TableBody = ({ children }) => {
  return <tbody>{children}</tbody>;
};

export const TableRow = ({ children }) => {
  return <tr>{children}</tr>;
};

export const TableCell = ({ children }) => {
  return <td>{children}</td>;
};
